// src/views/enseignant/EnseignantLayout.jsx
import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useParams, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useAxios from "../../utils/useAxios";
import { tenantURL } from "../../utils/config";
import { EnseignantDrawerProvider } from "../../context/EnseignantDrawerContext";
import EnseignantDrawer from "../../components/EnseignantDrawer";

// Pages Enseignant
import ENAccueil from "./ENAccueil";
import ENFaireAppel from "./ENFaireAppel";
import ENHistoriquePointages from "./ENHistoriquePointages";
import ENProfil from "./ENProfil";

// ─── MODAL CONFIRMATION DÉCONNEXION ───
function LogoutDialog({ onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal-box" style={{ maxWidth: 340 }}>
        <div className="modal-title">
          Déconnexion
          <button className="ibtn" style={{ border: "none" }} onClick={onCancel}>
            <i className="ti ti-x" />
          </button>
        </div>
        <div style={{ fontSize: 15, color: "var(--m)", lineHeight: 1.5, marginBottom: 24 }}>
          Voulez-vous vraiment vous déconnecter ?
        </div>
        <div className="factions">
          <button className="bsec" onClick={onCancel}>Annuler</button>
          <button className="bprim rd" onClick={onConfirm}>Déconnecter</button>
        </div>
      </div>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL (wrapper : fournit le contexte du drawer) ────────
export default function EnseignantLayout() {
  return (
    <EnseignantDrawerProvider>
      <EnseignantLayoutContent />
    </EnseignantDrawerProvider>
  );
}

// ─── CONTENU DU LAYOUT (consomme le contexte du drawer) ───────────────────
function EnseignantLayoutContent() {
  const { user, logoutUser } = useAuth();
  const { slug } = useParams();
  const navigate = useNavigate();
  const api = useAxios();

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [username, setUsername] = useState("Enseignant");
  const [badges, setBadges] = useState({ notifications: 0 });

  const getEnseignantPath = (path) => {
    if (!slug) return "/login";
    return `/${slug}/enseignant/${path}`;
  };

  // ─── CHARGEMENT DES DONNÉES ────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;

      try {
        const [userRes] = await Promise.all([
          api.get(tenantURL(slug, 'enseignant/profil/')).catch(() => ({ data: { username: "Enseignant" } })),
        ]);

        setUsername(userRes.data?.username || "Enseignant");
      } catch (error) {
      }
    };

    fetchData();
  }, [slug]);

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logoutUser();
  };

  if (!slug) {
    return (
      <div className="loader-container">
        <div style={{ textAlign: "center", color: "var(--m)" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏫</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Chargement...</div>
          <div style={{ fontSize: 14 }}>Aucun établissement trouvé</div>
        </div>
      </div>
    );
  }

  // ─── RENDU ──────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>

      {/* DRAWER GLOBAL — rendu une seule fois, piloté par le contexte,
          n'importe quelle page peut l'ouvrir via useEnseignantDrawer().openDrawer() */}
      <EnseignantDrawer
        username={username}
        badges={badges}
        getEnseignantPath={getEnseignantPath}
        onNavigate={navigate}
        onLogoutClick={() => setShowLogoutConfirm(true)}
      />

      {/* MODALS */}
      {showLogoutConfirm && (
        <LogoutDialog onConfirm={handleLogout} onCancel={() => setShowLogoutConfirm(false)} />
      )}

      {/* CONTENU PRINCIPAL */}
      <div className="app">
        {/* ✅ Topbar sans avatar ni titre — chaque page gère son propre header */}
        <header className="enseignant-topbar" />

        <main className="main">
          <div className="content">
            <Routes>
              <Route index element={<Navigate to="accueil" replace />} />
              <Route path="accueil" element={<ENAccueil slug={slug} />} />
              <Route path="faire-appel" element={<ENFaireAppel slug={slug} />} />
              <Route path="historique" element={<ENHistoriquePointages slug={slug} />} />
              <Route path="profil" element={<ENProfil slug={slug} />} />
            </Routes>
          </div>
        </main>
      </div>
    </>
  );
}

// ─── CSS ──────────────────────────────────────────────────────────────
// Aligné sur le thème Kamalpha de ParentLayout.jsx : mêmes variables
// (bleu profond + or du logo), mêmes polices, même style de drawer.
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Cinzel:wght@600;700&display=swap');
@import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  /* Bleu du logo */
  --pr:    #1B4F80;
  --pr-lt: #2F6DA8;
  --pr-dk: #0E2F52;
  --pr-bg: #E6EEF7;

  /* Or du logo (identité uniquement : jamais pour signaler un statut) */
  --or:    #C08A2E;
  --or-lt: #E3B75A;
  --or-dk: #8A5A14;

  --bl: #1a6fff; --bl-bg: #e8f1ff; --bl-dk: #0c447c;
  --tl: #0f6e56; --tl-bg: #e1f5ee;
  --am: #ba7517; --am-bg: #faeeda;
  --rd: #dc3545; --rd-dk: #b02a37; --rd-bg: #fcebeb;
  --gn: #3b6d11; --gn-bg: #eaf3de;
  --s:  #ffffff;
  --s2: #f5f6fa;
  --b:  #e8e8ee;
  --b2: #d0d0da;
  --t:  #0f1b2d;
  --m:  #64748b;
  --h:  #9ba5b4;
  --radius-lg: 10px;
  --radius-md: 7px;
}

body {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: var(--s2);
  font-size: 16px;
  color: var(--t);
  margin: 0;
}

.app { display: flex; flex-direction: column; min-height: 100vh; }

/* ─── TOPBAR ENSEIGNANT (vide) ─── */
.enseignant-topbar {
  display: none;
}

/* ─── DRAWER ─── */
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 99;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}
.drawer-overlay.open {
  opacity: 1;
  pointer-events: auto;
}

.drawer {
  position: fixed;
  top: 0;
  left: -280px;
  width: 280px;
  height: 100%;
  background: var(--s);
  z-index: 100;
  box-shadow: 2px 0 20px rgba(0,0,0,0.15);
  transition: left 0.3s ease;
  display: flex;
  flex-direction: column;
}
.drawer.open {
  left: 0;
}

.drawer-header {
  background: linear-gradient(135deg, var(--pr-dk), var(--pr));
  padding: 32px 20px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  position: relative;
}

.drawer-header::after {
  content: "";
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--or), transparent);
}

.drawer-logo-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid rgba(192, 138, 46, 0.5);
}

.drawer-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.drawer-brand {
  display: flex;
  flex-direction: column;
}

.drawer-title {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.drawer-subtitle {
  color: var(--or-lt);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.drawer-nav {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
}

.drawer-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 12px 16px;
}

.drawer-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--pr-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.drawer-avatar i {
  font-size: 22px;
  color: var(--pr);
}

.drawer-user-info {
  display: flex;
  flex-direction: column;
}

.drawer-username {
  font-weight: 600;
  font-size: 15px;
  color: var(--t);
}

.drawer-user-role {
  font-size: 12px;
  color: var(--m);
}

.drawer-divider {
  height: 1px;
  background: var(--b);
  margin: 8px 6px;
}

.ni-drawer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: var(--t);
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;
  font-family: inherit;
}

.ni-drawer i {
  font-size: 18px;
  width: 20px;
  flex-shrink: 0;
  color: var(--pr);
}

.ni-drawer.active {
  background: var(--pr-bg);
  color: var(--pr);
  font-weight: 600;
  box-shadow: inset 3px 0 0 var(--or);
}

.ni-drawer.active i {
  color: var(--pr);
}

.ni-drawer:hover {
  background: var(--s2);
}

.drawer-label {
  flex: 1;
}

.drawer-badge {
  background: var(--rd);
  color: #fff;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.drawer-logout-wrapper {
  margin: 16px 0 8px;
}

.drawer-logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: none;
  border: 1px solid rgba(220, 53, 69, 0.35);
  cursor: pointer;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: var(--rd);
  transition: background 0.15s;
}

.drawer-logout-btn:hover {
  background: rgba(220, 53, 69, 0.07);
}

.drawer-logout-btn i {
  font-size: 17px;
}

.drawer-version {
  text-align: center;
  padding: 16px 0;
  color: var(--h);
  font-size: 12px;
}

/* ─── MAIN ─── */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.content { flex: 1; overflow-y: auto; padding: 0; }

/* ─── CLASSES UTILITAIRES ─── */
.ibtn {
  width: 30px; height: 30px; border-radius: 5px;
  border: 0.5px solid var(--b); background: var(--s);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--m); font-size: 17px;
  transition: all .12s;
}
.ibtn:hover { background: var(--s2); color: var(--t); }

.bprim {
  display: flex; align-items: center; gap: 5px;
  padding: 8px 15px; background: var(--pr); color: #fff;
  border: none; border-radius: 5px; font-size: 15px;
  cursor: pointer; font-family: inherit; font-weight: 500;
  white-space: nowrap; transition: opacity .12s;
}
.bprim:hover { opacity: .88; }
.bprim.rd { background: var(--rd); }

.bsec {
  display: flex; align-items: center; gap: 5px;
  padding: 8px 13px; background: var(--s); color: var(--m);
  border: 0.5px solid var(--b2); border-radius: 5px;
  font-size: 15px; cursor: pointer; font-family: inherit;
  transition: all .12s;
}
.bsec:hover { background: var(--s2); color: var(--t); }

.factions { display: flex; gap: 7px; margin-top: 12px; justify-content: flex-end; }

.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35);
  z-index: 200; display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.modal-box {
  background: var(--s); border-radius: var(--radius-lg);
  border: 0.5px solid var(--b); padding: 18px;
  width: 450px; max-width: 100%; max-height: 90vh; overflow-y: auto;
}
.modal-title {
  font-size: 17px; font-weight: 600; color: var(--t);
  margin-bottom: 13px; display: flex; align-items: center; justify-content: space-between;
}

.loader-container { display: flex; justify-content: center; align-items: center; min-height: 300px; width: 100%; }
.loader-spinner { width: 48px; height: 48px; border: 4px solid var(--s2); border-top: 4px solid var(--pr); border-radius: 50%; animation: spin 0.8s linear infinite; }

@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: var(--s2); }
::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: var(--m); }
`;